import { generateMockMetricData, generateMockChartData, generateMockTableData, generateMockInsightData } from './mockData';

// MetricCategory and DataSourceType type definitions (converted from TypeScript)
// // export type MetricCategory = 'emotional-state' | 'emotion-response' | 'context' | 'check-in-rate';
// // export type DataSourceType = 'metric' | 'time-series' | 'tabular' | 'insight';

// MockDataSource interface (converted from TypeScript)
// export interface MockDataSource {
//   id: string;
//   label: string;
//   description: string;
//   metricCategory: MetricCategory;
//   availableContexts: string[];
//   availablePeriods: string[];
//   dataType: DataSourceType;
//   generator: (config: DataSourceConfig) => any;
//   defaultColor?: string;
// }

// 44 Sample Emotion Responses (representative set)
const EMOTION_RESPONSES = [
  { id: 'happy', label: 'Happy', color: '#FFD700' },
  { id: 'excited', label: 'Excited', color: '#FF6B35' },
  { id: 'calm', label: 'Calm', color: '#4ECDC4' },
  { id: 'content', label: 'Content', color: '#95E1D3' },
  { id: 'joyful', label: 'Joyful', color: '#F38181' },
  { id: 'peaceful', label: 'Peaceful', color: '#AAE3E2' },
  { id: 'confident', label: 'Confident', color: '#FFA07A' },
  { id: 'grateful', label: 'Grateful', color: '#FFB347' },
  { id: 'hopeful', label: 'Hopeful', color: '#87CEEB' },
  { id: 'proud', label: 'Proud', color: '#FF69B4' },
  { id: 'sad', label: 'Sad', color: '#4169E1' },
  { id: 'anxious', label: 'Anxious', color: '#FF6347' },
  { id: 'worried', label: 'Worried', color: '#FF4500' },
  { id: 'frustrated', label: 'Frustrated', color: '#DC143C' },
  { id: 'angry', label: 'Angry', color: '#8B0000' },
  { id: 'scared', label: 'Scared', color: '#4B0082' },
  { id: 'lonely', label: 'Lonely', color: '#696969' },
  { id: 'confused', label: 'Confused', color: '#9370DB' },
  { id: 'overwhelmed', label: 'Overwhelmed', color: '#8B0082' },
  { id: 'tired', label: 'Tired', color: '#708090' },
  { id: 'nervous', label: 'Nervous', color: '#CD5C5C' },
  { id: 'embarrassed', label: 'Embarrassed', color: '#FF1493' },
  { id: 'disappointed', label: 'Disappointed', color: '#4682B4' },
  { id: 'guilty', label: 'Guilty', color: '#556B2F' },
  { id: 'ashamed', label: 'Ashamed', color: '#2F4F4F' },
  { id: 'jealous', label: 'Jealous', color: '#228B22' },
  { id: 'envious', label: 'Envious', color: '#32CD32' },
  { id: 'resentful', label: 'Resentful', color: '#8B4513' },
  { id: 'hurt', label: 'Hurt', color: '#FF1493' },
  { id: 'rejected', label: 'Rejected', color: '#800080' },
  { id: 'isolated', label: 'Isolated', color: '#778899' },
  { id: 'abandoned', label: 'Abandoned', color: '#2F4F4F' },
  { id: 'neglected', label: 'Neglected', color: '#696969' },
  { id: 'insecure', label: 'Insecure', color: '#9370DB' },
  { id: 'vulnerable', label: 'Vulnerable', color: '#BA55D3' },
  { id: 'defensive', label: 'Defensive', color: '#CD853F' },
  { id: 'hostile', label: 'Hostile', color: '#8B0000' },
  { id: 'bitter', label: 'Bitter', color: '#654321' },
  { id: 'cynical', label: 'Cynical', color: '#708090' },
  { id: 'skeptical', label: 'Skeptical', color: '#778899' },
  { id: 'suspicious', label: 'Suspicious', color: '#8B7355' },
  { id: 'distrustful', label: 'Distrustful', color: '#696969' },
  { id: 'betrayed', label: 'Betrayed', color: '#8B0000' },
  { id: 'humiliated', label: 'Humiliated', color: '#4B0082' },
];

// Sample Contexts
const CONTEXTS = [
  { id: 'at-home', label: 'At home', color: '#4CAF50' },
  { id: 'in-the-classroom', label: 'In the classroom', color: '#2196F3' },
  { id: 'on-the-playground', label: 'On the playground', color: '#FF9800' },
  { id: 'online', label: 'Online', color: '#9C27B0' },
  { id: 'with-my-friends', label: 'With my friends', color: '#E91E63' },
  { id: 'on-the-bus', label: 'On the bus', color: '#00BCD4' },
  { id: 'playing-sport', label: 'Playing sport', color: '#FF5722' },
  { id: 'social-media-gaming', label: 'Social media / gaming', color: '#8BC34A' },
];

function createEmotionStateSource(id, label, color) {
  return {
    id,
    label,
    description: `Percentage of students in ${label.toLowerCase()} state`,
    metricCategory: 'emotional-state',
    availableContexts: ['all', 'at-home', 'in-the-classroom', 'on-the-playground', 'online', 'with-my-friends', 'on-the-bus', 'playing-sport', 'social-media-gaming'],
    availablePeriods: ['week', 'month', 'term', 'custom'],
    dataType: 'metric',
    defaultColor: color,
    generator: (config) => {
      const widgetConfig = {
        id: 'temp',
        type: 'metric',
        config: { metricType: 'emotional-state', selectedSourceId: id, ...config.filters },
        dataSources: [],
      };
      return generateMockMetricData(widgetConfig);
    },
  };
}

function createEmotionResponseSource(emotion) {
  return {
    id: `emotion-${emotion.id}`,
    label: emotion.label,
    description: `Percentage of students experiencing ${emotion.label.toLowerCase()}`,
    metricCategory: 'emotion-response',
    availableContexts: ['all', 'at-home', 'in-the-classroom', 'on-the-playground', 'online', 'with-my-friends', 'on-the-bus', 'playing-sport', 'social-media-gaming'],
    availablePeriods: ['week', 'month', 'term', 'custom'],
    dataType: 'metric',
    defaultColor: emotion.color,
    generator: (config) => {
      const widgetConfig = {
        id: 'temp',
        type: 'metric',
        config: { metricType: 'emotion-response', selectedSourceId: `emotion-${emotion.id}`, ...config.filters },
        dataSources: [],
      };
      return generateMockMetricData(widgetConfig);
    },
  };
}

function createContextSource(context) {
  return {
    id: `context-${context.id}`,
    label: context.label,
    description: `Data filtered by ${context.label.toLowerCase()} context`,
    metricCategory: 'context',
    availableContexts: ['all', 'at-home', 'in-the-classroom', 'on-the-playground', 'online', 'with-my-friends', 'on-the-bus', 'playing-sport', 'social-media-gaming'],
    availablePeriods: ['week', 'month', 'term', 'custom'],
    dataType: 'metric',
    defaultColor: context.color,
    generator: (config) => {
      const widgetConfig = {
        id: 'temp',
        type: 'metric',
        config: { metricType: 'context', selectedSourceId: `context-${context.id}`, ...config.filters },
        dataSources: [],
      };
      return generateMockMetricData(widgetConfig);
    },
  };
}

// Helper function to create weekly time-series sources for emotional states
function createWeeklyEmotionStateSource(
  id,
  weeklyId,
  label,
  color
) {
  return {
    id: weeklyId,
    label: label,
    description: `Trend of ${label.toLowerCase()} percentage`,
    metricCategory: 'emotional-state',
    availableContexts: ['all', 'at-home', 'in-the-classroom', 'on-the-playground', 'online', 'with-my-friends', 'on-the-bus', 'playing-sport', 'social-media-gaming'],
    availablePeriods: ['week', 'month', 'term', 'custom'],
    dataType: 'time-series',
    defaultColor: color,
    generator: (config) => {
      const widgetConfig = {
        id: 'temp',
        type: 'line-chart',
        dataSources: [{
          ...config,
          sourceId: id, // Use the base emotional state ID for data generation
        }],
        config: {},
      };
      return generateMockChartData(widgetConfig);
    },
  };
}

export const AVAILABLE_DATA_SOURCES: MockDataSource[] = [
  // Emotional States (5 states)
  createEmotionStateSource('ready-to-learn', 'Ready to Learn', '#4CAF50'),
  createEmotionStateSource('processing', 'In a state of processing', '#2196F3'),
  createEmotionStateSource('heightened-emotion', 'Heightened state of emotion', '#FF9800'),
  createEmotionStateSource('not-ready-to-learn', 'Not ready to learn', '#FFC107'),
  createEmotionStateSource('emotional-overload', 'Emotional overload', '#9C27B0'),

  // Emotion Responses (44 emotions)
  ...EMOTION_RESPONSES.map(createEmotionResponseSource),

  // Contexts (10 contexts)
  ...CONTEXTS.map(createContextSource),

  // Check-in Rate (single source)
  {
    id: 'check-in-rate',
    label: 'Check-In Rate',
    description: 'Percentage of students who have checked in (with minimum days per week threshold)',
    metricCategory: 'check-in-rate',
    availableContexts: ['all', 'at-home', 'in-the-classroom', 'on-the-playground', 'online', 'with-my-friends', 'on-the-bus', 'playing-sport', 'social-media-gaming'],
    availablePeriods: ['week', 'month', 'term', 'custom'],
    dataType: 'metric',
    defaultColor: '#2196F3',
    generator: (config) => {
      const widgetConfig = {
        id: 'temp',
        type: 'metric',
        config: { metricType: 'check-in-rate', ...config.filters },
        dataSources: [],
      };
      return generateMockMetricData(widgetConfig);
    },
  },

  // Weekly time-series sources for emotional states (for line charts)
  createWeeklyEmotionStateSource('ready-to-learn', 'weeklyRTL', 'Ready to Learn', '#4CAF50'),
  createWeeklyEmotionStateSource('processing', 'weeklyPROC', 'In a state of processing', '#2196F3'),
  createWeeklyEmotionStateSource('heightened-emotion', 'weeklyHE', 'Heightened state of emotion', '#FF9800'),
  createWeeklyEmotionStateSource('not-ready-to-learn', 'weeklyNRTL', 'Not ready to learn', '#FFC107'),
  createWeeklyEmotionStateSource('emotional-overload', 'weeklyEO', 'Emotional overload', '#9C27B0'),
  
  // Performance Sources (for tables)
  {
    id: 'student-readiness-table',
    label: 'Student Readiness Data',
    description: 'Tabular data of student readiness metrics',
    metricCategory: 'emotional-state',
    availableContexts: ['all', 'at-home', 'in-the-classroom', 'on-the-playground', 'online', 'with-my-friends', 'on-the-bus', 'playing-sport', 'social-media-gaming'],
    availablePeriods: ['week', 'month', 'term'],
    dataType: 'tabular',
    defaultColor: '#607D8B',
    generator: (config) => {
      const widgetConfig = {
        id: 'temp',
        type: 'table',
        config: {
          columns: ['Name', 'Class', 'Ready %', 'Processing %', 'Not Ready %', 'Score'],
          rowCount: 20,
          ...config.filters,
        },
        dataSources: [],
      };
      return generateMockTableData(widgetConfig);
    },
  },
  {
    id: 'class-performance-table',
    label: 'Class Performance Data',
    description: 'Tabular data of class-level performance metrics',
    metricCategory: 'emotional-state',
    availableContexts: ['all'],
    availablePeriods: ['week', 'month', 'term'],
    dataType: 'tabular',
    defaultColor: '#607D8B',
    generator: (config) => {
      const widgetConfig = {
        id: 'temp',
        type: 'table',
        config: {
          columns: ['Class', 'Students', 'Ready %', 'Engagement', 'Status'],
          rowCount: 10,
          ...config.filters,
        },
        dataSources: [],
      };
      return generateMockTableData(widgetConfig);
    },
  },
];

export function getDataSourceById(id): MockDataSource | undefined {
  return AVAILABLE_DATA_SOURCES.find((source) => source.id === id);
}

export function getDataSourcesByMetricCategory(category) {
  return AVAILABLE_DATA_SOURCES.filter((source) => source.metricCategory === category);
}

export function getDataSourcesByType(dataType) {
  return AVAILABLE_DATA_SOURCES.filter((source) => source.dataType === dataType);
}

// Legacy function for backward compatibility
export function getDataSourcesByCategory(category): MockDataSource[] {
  // Map old categories to new metric categories
  const categoryMap = {
    'emotion': 'emotional-state',
    'engagement': 'check-in-rate',
    'readiness': 'emotional-state',
    'performance': 'emotional-state',
  };
  const metricCategory = categoryMap[category] || 'emotional-state';
  return getDataSourcesByMetricCategory(metricCategory);
}
